// betaSignup.service.ts
//
// Thin wrapper around POST /auth/checkout on the AeroParts AI backend.
// Replaces the old Supabase Edge Function approach.

const API_URL = process.env.NEXT_PUBLIC_API_URL ?? '';

export type BillingInterval = 'monthly' | 'yearly-monthly' | 'yearly';

export interface CheckoutRequest {
  email: string;
  companyName?: string;
  billingInterval: BillingInterval;
  trialDays: number;
}

export interface CheckoutResponse {
  sessionId: string;
  sessionUrl: string;
}

export const betaSignupService = {
  /**
   * Creates a Stripe Checkout session via the backend.
   * Returns the Stripe-hosted sessionUrl to redirect the user to.
   */
  async createCheckout(data: CheckoutRequest): Promise<CheckoutResponse> {
    if (!API_URL) {
      throw new Error('API URL is not configured. Please contact support.');
    }

    const res = await fetch(`${API_URL}/auth/checkout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    const contentType = res.headers.get('content-type') ?? '';
    if (!contentType.includes('application/json')) {
      throw new Error(`Server error (${res.status}). Please try again or contact support.`);
    }

    const body = await res.json();

    if (!res.ok) {
      const message = body?.error ?? 'Failed to create checkout session';
      throw new Error(typeof message === 'string' ? message : JSON.stringify(message));
    }

    if (!body?.sessionUrl) {
      throw new Error('No checkout URL returned from server. Please try again.');
    }

    return body as CheckoutResponse;
  },
};
